import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CommercialEntityForm from './CommercialEntityForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import FilterBanner from '../../../shared/FilterBanner';
import PricelistSentFilterFields, { DEFAULT_PRICELIST_SENT_FILTERS, pricelistSentParams, pricelistSentFilterKey } from '../../../shared/PricelistSentFilterFields';
import CancelButton from '../../../shared/CancelButton';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { PlaceholderThumb, PersonPlaceholderThumb } from '../../../shared/ProductThumb';
import { Pill, CategoryPill, ActiveStatusPill, GoogleLookupPill } from '../../../shared/Pill';
import { LOGIN_STATUS } from './CustomerLoginsCard';
import { QuickBooksIssuePills } from '../../../shared/QuickBooksCustomerPicker';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import FieldHelp from '../../../shared/FieldHelp';
import CustomerPriceGroupMatrix from '../../sales/Customers/CustomerPriceGroupMatrix';
import { formatAddress } from '../../../shared/formatAddress';
import { formatDate } from '../../../shared/tenantClock';
import { useClassificationOptions, ClassificationFilterFields, PrimaryClassificationCell } from '../../../shared/OrganisationClassification';
import { Filter as FilterIcon } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const TYPE_LABELS = { organization: 'Organisation', individual: 'Individual' };

// Optional list-table columns (Columns picker, persisted per-user via
// /api/column-preferences) — Logo/Photo, Name and actions stay always-visible
// (SYSTEM_COLUMNS) since they identify the row, not display preference. Tier, Price Groups, Tax
// Handling and Terms can be blank on a Prospect — they're only required from
// a Stage that requires commercial terms (Opportunity by default).
const OPTIONAL_COLUMNS = [
  { key: 'type', label: 'Shape', group: 'Details' },
  { key: 'legal_entity', label: 'Legal Entity', group: 'Details' },
  { key: 'source', label: 'Source', group: 'Details' },
  { key: 'stage', label: 'Stage', group: 'Relationship' },
  // Why a closed Prospect was closed (issue #246).
  { key: 'close_reason', label: 'Close Reason', group: 'Relationship' },
  { key: 'market', label: 'Market', group: 'Classification' },
  { key: 'segment', label: 'Segment', group: 'Classification' },
  { key: 'operator', label: 'Operator', group: 'Classification' },
  { key: 'channel', label: 'Outlet', group: 'Classification' },
  { key: 'tier', label: 'Tier', group: 'Classification' },
  { key: 'price_group', label: 'Price Groups', group: 'Trading' },
  { key: 'tax_handling', label: 'Tax Handling', group: 'Trading' },
  { key: 'terms', label: 'Terms', group: 'Trading' },
  // When the Customer started trading with the tenant (the portal's
  // "Ordering with us since").
  { key: 'customer_since', label: 'Customer Since', group: 'Trading' },
  // Most recent Pricelist sent, any scope — pairs with the Pricelist Sent
  // filter for working through a new Pricelist's send-out.
  { key: 'last_pricelist_sent', label: 'Last Pricelist Sent', group: 'Trading' },
  // Customer portal logins (issue #253): how many, with the one status that
  // needs attention; hover lists each email and its status.
  { key: 'logins', label: 'Logins', group: 'Trading' },
  { key: 'address', label: 'Address', group: 'Location & Contact' },
  { key: 'state', label: 'State', group: 'Location & Contact' },
  { key: 'country', label: 'Country', group: 'Location & Contact' },
  { key: 'phone', label: 'Phone', group: 'Location & Contact' },
  { key: 'owner', label: 'Account Owner', group: 'Admin' },
  { key: 'sync', label: 'Email Sync', group: 'Admin' },
  // Rolled-up Opening Hours/Address Google Places lookup state — one pill,
  // stale if either field is (see GoogleLookupPill), split back out into
  // two field-specific pills on the detail page where there's room to be
  // specific about which one needs it.
  { key: 'google_sync', label: 'Address/Hours', group: 'Admin' },
  // The QuickBooks Customer it's linked to, and anything needing attention.
  { key: 'quickbooks', label: 'QuickBooks Customer', group: 'Admin' },
  { key: 'status', label: 'Status', group: 'Admin' },
];
const SYSTEM_COLUMNS = ['Logo/Photo', 'Name', 'Actions'];
// Each screen's everyday job, about six columns (ui-architecture.md Column
// Picker) — everything else is one tick away in Columns, and each user's
// own choice there sticks. Sales > Customers is for working with
// Customers; CRM's Prospects & Customers is for working the pipeline, where
// Tier and Logins are blank on every Prospect.
const CUSTOMERS_DEFAULT_VISIBLE_COLUMNS = ['tier', 'state', 'phone', 'last_pricelist_sent', 'logins', 'status'];
const PIPELINE_DEFAULT_VISIBLE_COLUMNS = ['market', 'stage', 'state', 'phone', 'last_pricelist_sent', 'status'];
const QUICKBOOKS_FILTER_LABELS = { matched: 'Matched', unmatched: 'Unmatched', needs_attention: 'Needs attention' };
const LOGIN_FILTER_LABELS = { has: 'Has logins', none: 'No logins', invite_expired: 'Invite expired', switched_off: 'Switched off' };

// The Logins cell: the count, plus the one status worth a look — expired
// invites first, then a paused Customer, then invites waiting, then every
// login switched off. Nothing extra when they're all fine.
function LoginsCell({ logins }) {
  if (!logins?.length) return '—';
  const count = (status) => logins.filter((login) => login.status === status).length;
  let hint = null;
  if (count('invite_expired')) hint = { status: 'invite_expired', label: `${count('invite_expired')} expired` };
  else if (count('paused')) hint = { status: 'paused', label: 'Paused' };
  else if (count('invited')) hint = { status: 'invited', label: `${count('invited')} invited` };
  else if (count('switched_off') === logins.length) hint = { status: 'switched_off', label: 'Off' };
  const title = logins
    .map((login) => `${login.email} — ${LOGIN_STATUS[login.status]?.label || login.status}${login.status_reason ? ` (${login.status_reason})` : ''}`)
    .join('\n');
  const meta = hint && LOGIN_STATUS[hint.status];
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {logins.length}
      {hint && <Pill color={meta.color} background={meta.background}>{hint.label}</Pill>}
    </span>
  );
}

// Identifies this screen's saved filters and column preferences — the
// Customers key, kept when Leads and Customers merged into one list (the
// Leads views were moved onto it by migration).
const FILTER_SCREEN_KEY = 'customers';
// `lifecycle` is '' (every Prospect and Customer), 'prospects' or
// 'customers' — split by Relationship Type, since a record is never both
// (vis_relationship_lifecycle.md §6). Prospects and Customers are System
// tabs over it.
const DEFAULT_FILTERS = { status: 'all', lifecycle: '', stage_id: '', close_reason_id: '', type: '', market_id: '', market_segment_id: '', operator_id: '', channel_id: '', country: '', source_id: '', legal_entity_type_id: '', owner: '', sync: '', customer_price_group_id: '', product_price_group_id: '', logins: '', quickbooks: '', ...DEFAULT_PRICELIST_SENT_FILTERS };
const LIFECYCLE_TABS = [
  { key: 'prospects', label: 'Prospects', filters: { lifecycle: 'prospects' } },
  { key: 'customers', label: 'Customers', filters: { lifecycle: 'customers' } },
];
const LIFECYCLES = ['prospects', 'customers'];
const CUSTOMERS_ONLY_DEFAULT_FILTERS = { ...DEFAULT_FILTERS, lifecycle: 'customers' };
const LIFECYCLE_TYPE_KEYS = ['prospect', 'customer'];
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
// List / Price Groups view toggle — same segmented look as
// DashboardCalendar.js's Month/Week toggle.
const toggleBtnStyle = (active) => ({
  border: '1px solid #b3bbc7',
  background: active ? '#1976d2' : '#fff',
  color: active ? '#fff' : '#333',
  fontSize: 13,
  fontWeight: 600,
  padding: '5px 12px',
  cursor: 'pointer',
});

// This row's own Prospect or Customer relationship — it holds one or the
// other, never both.
function lifecycleRelationship(item) {
  return (item.relationships || []).find((r) => LIFECYCLE_TYPE_KEYS.includes(r.relationship_type?.system_key));
}

// Every other Relationship Type this record holds besides Prospect/Customer
// — the old tier_id model couldn't show this at all, since an entity could
// only ever be a Lead/Customer, nothing else at the same time.
function otherRelationshipTypeNames(item) {
  return (item.relationships || [])
    .filter((r) => !LIFECYCLE_TYPE_KEYS.includes(r.relationship_type?.system_key))
    .map((r) => r.relationship_type?.name)
    .filter(Boolean);
}

// Prospects & Customers — one list over every Commercial Entity holding a
// Prospect or Customer relationship. Default shows them all; the Prospects
// and Customers System tabs narrow by lifecycle. /customer-customers opens on the
// Customers tab (initialLifecycle), and ?lifecycle= does the same for a
// return trip from a record.
//
// customersOnly is Sales > Customers: a Customers list in its own right,
// headed Customers, locked to the Customer lifecycle, with no Prospects/
// Customers tabs or filter, and its own saved tabs and column preferences
// (screen key 'sales-customers'). Listing reads /api/customers (scope=lifecycle);
// Add goes through /api/commercial-entities. Row click / View both go to the
// dedicated detail page (CommercialEntityDetailPage.js) — the inline form
// here is Add-only.
function CommercialEntityTable({ initialLifecycle, customersOnly = false }) {
  const { canCreate } = useScreenAccess();
  const screenKey = customersOnly ? 'sales-customers' : FILTER_SCREEN_KEY;
  const defaultFilters = customersOnly ? CUSTOMERS_ONLY_DEFAULT_FILTERS : DEFAULT_FILTERS;
  const heading = customersOnly ? 'Customers' : 'Prospects & Customers';
  const defaultVisibleColumns = customersOnly ? CUSTOMERS_DEFAULT_VISIBLE_COLUMNS : PIPELINE_DEFAULT_VISIBLE_COLUMNS;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [startLifecycle] = useState(() => {
    if (customersOnly) return 'customers';
    const fromUrl = searchParams.get('lifecycle');
    const start = initialLifecycle || fromUrl || '';
    return LIFECYCLES.includes(start) ? start : '';
  });
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  // Which row's stale pill is mid-refresh — a click on either the Hours
  // Stale or Address Stale pill runs the same combined Google Places
  // lookup (one call resolves whichever is actually stale), same endpoint
  // as OpeningHoursCard.js's own "Look Up" button.
  const [refreshingId, setRefreshingId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // A dashboard tile can open this list already filtered (initialFilters).
  const [filters, setFilters] = useState(() => ({ ...defaultFilters, lifecycle: startLifecycle, ...(location.state?.initialFilters || {}) }));
  const { status, lifecycle, stage_id: stageId, close_reason_id: closeReasonId, type, market_id: marketId, market_segment_id: marketSegmentId, operator_id: operatorId, channel_id: channelId, country, source_id: sourceId, legal_entity_type_id: legalEntityTypeId, owner: ownerFilter, sync: syncFilter, customer_price_group_id: customerPriceGroupId, product_price_group_id: productPriceGroupId, logins: loginsFilter, quickbooks: quickbooksFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Free-text search, kept outside DEFAULT_FILTERS/SavedFilterTabs — same
  // split ProductTable.js/AllEntitiesTable.js use (search is unbounded free
  // text, not a saved filter value). Matches name, first name, last name or
  // a login email (backend's listCustomers).
  const [search, setSearch] = useState('');
  const [countryOptions, setCountryOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [legalEntityTypeOptions, setLegalEntityTypeOptions] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [closeReasonOptions, setCloseReasonOptions] = useState([]);
  const { markets: marketOptions, segments: segmentOptions, channels: channelOptions, operators: operatorOptions } = useClassificationOptions();
  const [customerPriceGroupOptions, setCustomerPriceGroupOptions] = useState([]);
  const [productPriceGroupOptions, setProductPriceGroupOptions] = useState([]);
  // List vs. Price Groups matrix view, kept in the URL so returning from a
  // record (breadcrumb/back) lands on the same view. The Price Groups view
  // only lists records that can be quoted or ordered.
  const view = searchParams.get('view') === 'price-groups' ? 'price_groups' : 'list';
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'price_groups') params.set('view', 'price-groups');
    else params.delete('view');
    setSearchParams(params);
    setPage(1);
  };
  const [loading, setLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showType = visibleColumns.includes('type');
  const showMarket = visibleColumns.includes('market');
  const showSegment = visibleColumns.includes('segment');
  const showOperator = visibleColumns.includes('operator');
  const showChannel = visibleColumns.includes('channel');
  const showStage = visibleColumns.includes('stage');
  const showCloseReason = visibleColumns.includes('close_reason');
  const showTier = visibleColumns.includes('tier');
  const showPriceGroup = visibleColumns.includes('price_group');
  const showTaxHandling = visibleColumns.includes('tax_handling');
  const showTerms = visibleColumns.includes('terms');
  const showState = visibleColumns.includes('state');
  const showCountry = visibleColumns.includes('country');
  const showAddress = visibleColumns.includes('address');
  const showPhone = visibleColumns.includes('phone');
  const showSource = visibleColumns.includes('source');
  const showLegalEntity = visibleColumns.includes('legal_entity');
  const showOwner = visibleColumns.includes('owner');
  const showGoogleSync = visibleColumns.includes('google_sync');
  const showSync = visibleColumns.includes('sync');
  const showLastPricelistSent = visibleColumns.includes('last_pricelist_sent');
  const showCustomerSince = visibleColumns.includes('customer_since');
  const showLogins = visibleColumns.includes('logins');
  const showQuickbooks = visibleColumns.includes('quickbooks');
  const showStatus = visibleColumns.includes('status');
  const totalCols = 3 // media, Name, actions
    + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length;

  // Ad hoc "Filters" panel — draft is null while closed, same pattern as
  // ProductTable.js's Filters button. Shares renderDrawerFields with
  // SavedFilterTabs below so the two never drift apart.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(defaultFilters).filter((k) => String(filters[k] || '') !== String(defaultFilters[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(defaultFilters); closeFilterPanel(); };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All items</option>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>
      {!customersOnly && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Prospects / Customers</label><br />
          <select className="styled-input" value={draft.lifecycle} onChange={(e) => setDraftField('lifecycle', e.target.value)} style={FIELD_INPUT_STYLE}>
            <option value="">All</option>
            <option value="prospects">Prospects</option>
            <option value="customers">Customers</option>
          </select>
        </div>
      )}
      <div>
        <label style={FIELD_LABEL_STYLE}>Stage</label><br />
        <select className="styled-input" value={draft.stage_id} onChange={(e) => setDraftField('stage_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All stages</option>
          {stageOptions.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      {!customersOnly && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Close Reason</label><br />
          <select className="styled-input" value={draft.close_reason_id} onChange={(e) => setDraftField('close_reason_id', e.target.value)} style={FIELD_INPUT_STYLE}>
            <option value="">All</option>
            {closeReasonOptions.map((r) => <option key={r.id} value={r.id}>{r.name}{r.is_active ? '' : ' (deactivated)'}</option>)}
          </select>
        </div>
      )}
      <div>
        <label style={FIELD_LABEL_STYLE}>Shape</label><br />
        <select className="styled-input" value={draft.type} onChange={(e) => setDraftField('type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All types</option>
          <option value="organization">Organisation</option>
          <option value="individual">Individual</option>
        </select>
      </div>
      <ClassificationFilterFields draft={draft} setDraftField={setDraftField} markets={marketOptions} segments={segmentOptions} channels={channelOptions} operators={operatorOptions} labelStyle={FIELD_LABEL_STYLE} inputStyle={FIELD_INPUT_STYLE} />
      <div>
        <label style={FIELD_LABEL_STYLE}>Country</label><br />
        <select className="styled-input" value={draft.country} onChange={(e) => setDraftField('country', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All countries</option>
          {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Source</label><br />
        <select className="styled-input" value={draft.source_id} onChange={(e) => setDraftField('source_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All sources</option>
          {sourceOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Legal Entity</label><br />
        <select className="styled-input" value={draft.legal_entity_type_id} onChange={(e) => setDraftField('legal_entity_type_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All legal entities</option>
          {legalEntityTypeOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Account Owner</label><br />
        <select className="styled-input" value={draft.owner} onChange={(e) => setDraftField('owner', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">Anyone</option>
          <option value="me">Me</option>
          <option value="none">No owner</option>
          {ownerOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Email Sync</label><br />
        <select className="styled-input" value={draft.sync} onChange={(e) => setDraftField('sync', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          <option value="synced">On</option>
          <option value="not_synced">Off</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer Price Group</label><br />
        <select className="styled-input" value={draft.customer_price_group_id} onChange={(e) => setDraftField('customer_price_group_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {customerPriceGroupOptions.map((g) => <option key={g.id} value={g.id}>{'  '.repeat(g.depth || 0)}{g.name}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Product Price Group</label><br />
        <select className="styled-input" value={draft.product_price_group_id} onChange={(e) => setDraftField('product_price_group_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {productPriceGroupOptions.filter((g) => g.is_active).map((g) => <option key={g.id} value={g.id}>{'  '.repeat(g.depth || 0)}{g.name}</option>)}
        </select>
      </div>
      <PricelistSentFilterFields draft={draft} setDraftField={setDraftField} labelStyle={FIELD_LABEL_STYLE} inputStyle={FIELD_INPUT_STYLE} />
      <div>
        <label style={FIELD_LABEL_STYLE}>Logins</label><br />
        <select className="styled-input" value={draft.logins} onChange={(e) => setDraftField('logins', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {Object.entries(LOGIN_FILTER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>QuickBooks</label><br />
        <select className="styled-input" value={draft.quickbooks || ''} onChange={(e) => setDraftField('quickbooks', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {Object.entries(QUICKBOOKS_FILTER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
    </>
  );

  // Drill-through from Payment Terms' "In Use" column
  // (PaymentTermTable.js) — a payment_term_id in the URL pre-filters this
  // list to the records effectively on that term (direct override or Tier
  // default, same resolution as customerCommercialsResolver.js).
  const paymentTermIdFilter = searchParams.get('payment_term_id');
  const paymentTermNameFilter = searchParams.get('payment_term_name');
  const clearPaymentTermFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('payment_term_id');
    next.delete('payment_term_name');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Customer Tiers' "In Use" column
  // (CustomerTierTable.js) — a tier_id in the URL pre-filters this list to
  // records directly assigned that Tier.
  const tierIdFilter = searchParams.get('tier_id');
  const tierNameFilter = searchParams.get('tier_name');
  const clearTierFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('tier_id');
    next.delete('tier_name');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from the Sales Dashboard's stale-Opening-Hours card
  // (SalesDashboard.js) — same "no prior filter support" shape as
  // payment_term_id/tier_id above, just a boolean flag rather than an id.
  const staleFilter = searchParams.get('opening_hours_stale') === 'true';
  const clearStaleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('opening_hours_stale');
    setSearchParams(next);
    setPage(1);
  };

  // Drill-through from Close Reasons' "In Use" column (CloseReasonTable.js).
  const closeReasonIdFilter = searchParams.get('close_reason_id');
  const closeReasonNameFilter = searchParams.get('close_reason_name');
  const clearCloseReasonFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('close_reason_id');
    next.delete('close_reason_name');
    setSearchParams(next);
    setPage(1);
  };

  const addressStaleFilter = searchParams.get('address_stale') === 'true';
  const clearAddressStaleFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('address_stale');
    setSearchParams(next);
    setPage(1);
  };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/commercial-entities/countries', { params: { scope: 'lifecycle' } })
      .then((res) => setCountryOptions(res.data.data || []))
      .catch(() => setCountryOptions([]));
    axios.get('/api/sources', { params: { status: 'active' } })
      .then((res) => setSourceOptions(res.data.data || []))
      .catch(() => setSourceOptions([]));
    axios.get('/api/legal-entity-types', { params: { status: 'active' } })
      .then((res) => setLegalEntityTypeOptions(res.data.data || []))
      .catch(() => setLegalEntityTypeOptions([]));
    axios.get('/api/users/account-owners')
      .then((res) => setOwnerOptions(res.data.data || []))
      .catch(() => setOwnerOptions([]));
    if (!customersOnly) {
      axios.get('/api/close-reasons', { params: { status: 'all' } })
        .then((res) => setCloseReasonOptions(res.data.data || []))
        .catch(() => setCloseReasonOptions([]));
    }
    axios.get('/api/customer-price-groups', { params: { status: 'active' } })
      .then((res) => setCustomerPriceGroupOptions(res.data.data || []))
      .catch(() => setCustomerPriceGroupOptions([]));
    axios.get('/api/product-price-groups')
      .then((res) => setProductPriceGroupOptions(res.data.data || []))
      .catch(() => setProductPriceGroupOptions([]));
    // Prospect's and Customer's Stages, each in order — the Stage filter's
    // options, grouped by Type.
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => {
        const types = res.data.data || [];
        setStageOptions((customersOnly ? ['customer'] : LIFECYCLE_TYPE_KEYS)
          .map((key) => types.find((t) => t.system_key === key))
          .filter(Boolean)
          .map((t) => ({ label: t.name, stages: t.stages || [] })));
      })
      .catch(() => setStageOptions([]));
  }, [customersOnly]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, status, lifecycle, stageId, closeReasonId, closeReasonIdFilter, type, marketId, marketSegmentId, operatorId, channelId, country, sourceId, legalEntityTypeId, ownerFilter, syncFilter, customerPriceGroupId, productPriceGroupId, loginsFilter, quickbooksFilter, page, limit, view, paymentTermIdFilter, tierIdFilter, staleFilter, addressStaleFilter, pricelistSentFilterKey(filters)]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { scope: 'lifecycle', status, page, limit };
      if (lifecycle) params.lifecycle = lifecycle;
      if (stageId) params.stage_id = stageId;
      // The In Use drill-through from Close Reasons wins over the Filters
      // panel's own pick.
      if (closeReasonIdFilter || closeReasonId) params.close_reason_id = closeReasonIdFilter || closeReasonId;
      if (view === 'price_groups') params.quotable_only = 'true';
      if (search) params.search = search;
      if (type) params.type = type;
      if (marketId) params.market_id = marketId;
      if (marketSegmentId) params.market_segment_id = marketSegmentId;
      if (operatorId) params.operator_id = operatorId;
      if (channelId) params.channel_id = channelId;
      if (country) params.country = country;
      if (sourceId) params.source_id = sourceId;
      if (legalEntityTypeId) params.legal_entity_type_id = legalEntityTypeId;
      if (ownerFilter) params.owner = ownerFilter;
      if (syncFilter) params.sync = syncFilter;
      if (loginsFilter) params.logins = loginsFilter;
      if (quickbooksFilter) params.quickbooks = quickbooksFilter;
      // Product Price Group only narrows the server filter when paired with
      // a Customer Price Group; on its own it just narrows the matrix's
      // columns (every record still listed, so gaps stay visible).
      if (customerPriceGroupId) {
        params.customer_price_group_id = customerPriceGroupId;
        if (productPriceGroupId) params.product_price_group_id = productPriceGroupId;
      }
      if (paymentTermIdFilter) params.payment_term_id = paymentTermIdFilter;
      if (tierIdFilter) params.tier_id = tierIdFilter;
      if (staleFilter) params.opening_hours_stale = 'true';
      if (addressStaleFilter) params.address_stale = 'true';
      Object.assign(params, pricelistSentParams(filters));
      const res = await axios.get('/api/customers', { params });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setShowForm(true);
  };

  // Back from the record returns to this list — on whichever screen it was
  // opened from (CRM, or Sales > Customers) — on the same lifecycle tab and
  // view.
  const originPath = () => {
    const params = new URLSearchParams();
    if (lifecycle) params.set('lifecycle', lifecycle);
    if (view === 'price_groups') params.set('view', 'price-groups');
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const handleView = (item) => {
    navigate(`/commercial-entities/${item.id}`, { state: { browseIds: rows.map((r) => r.id), origin: { label: heading, path: originPath() } } });
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/commercial-entities/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/commercial-entities/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/commercial-entities/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  // One-directional (Individual -> Organisation only, see
  // commercialEntityController.convertToOrganisation) — nulls First/Last
  // Name, everything else (Tier, Price Group, Addresses, etc.) carries
  // straight over unchanged.
  const handleConvertToOrganisation = async (item) => {
    if (!(await askConfirm(`Convert "${item.name}" from an Individual to an Organisation? First/Last Name will be cleared; this can't be undone from here.`, { confirmLabel: 'Convert' }))) return;
    try {
      await axios.patch(`/api/commercial-entities/${item.id}/convert-to-organisation`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Convert to Organisation failed');
    }
  };

  const handleRefreshStale = async (e, item) => {
    e.stopPropagation();
    if (refreshingId) return;
    setRefreshingId(item.id);
    try {
      const res = await axios.post(`/api/commercial-entities/${item.id}/opening-hours/lookup`);
      // Merge rather than replace — the not-found branch returns a
      // thinner entity (no relationships/logo/tier includes) than the
      // list row it's updating, so a full replace would blank those
      // columns out for this row until the next fetchRows().
      setRows((prev) => prev.map((r) => (r.id === item.id ? { ...r, ...res.data.data } : r)));
      dismissError();
    } catch (err) {
      showError(err, 'Look up failed');
    }
    setRefreshingId(null);
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      await axios.post('/api/commercial-entities', data);
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  // On the Customers tab Add creates a Customer at Active (Tier and
  // Customer Price Group required); everywhere else it starts a Prospect at
  // Lead.
  const addingCustomer = lifecycle === 'customers';
  const totalLabel = lifecycle === 'prospects' ? 'Prospects' : lifecycle === 'customers' ? 'Customers' : 'Prospects & Customers';

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>{heading}</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              {addingCustomer ? 'Add Customer' : 'Add Prospect'}
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search name or login email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 200 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        {/* Kept mounted (hidden) in the Price Groups view so its saved
            column preferences still load into visibleColumns. */}
        <div style={{ display: view === 'list' ? undefined : 'none' }}>
          <ColumnPicker
            screenKey={screenKey}
            columns={OPTIONAL_COLUMNS}
            defaultVisible={defaultVisibleColumns}
            onChange={setVisibleColumns}
            tabColumns={tabColumns}
            systemColumns={SYSTEM_COLUMNS}
          />
        </div>
        <div style={{ display: 'flex', marginLeft: 'auto' }}>
          <button type="button" style={{ ...toggleBtnStyle(view === 'list'), borderRadius: '4px 0 0 4px' }} onClick={() => setView('list')}>List</button>
          <button type="button" title="Show the records that can be quoted or ordered, by Price Group" style={{ ...toggleBtnStyle(view === 'price_groups'), borderRadius: '0 4px 4px 0', borderLeft: 'none' }} onClick={() => setView('price_groups')}>Price Groups</button>
        </div>
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={screenKey}
          tabColumns={tabColumns}
          defaultFilters={defaultFilters}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
          systemTabs={customersOnly ? [] : LIFECYCLE_TABS}
          initialSelectedKey={customersOnly ? 'default' : (startLifecycle || 'default')}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description={`Narrow the ${heading} list.`}
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {paymentTermIdFilter && (
        <FilterBanner
          message={`Showing records on payment term "${paymentTermNameFilter || paymentTermIdFilter}"`}
          onClear={clearPaymentTermFilter}
        />
      )}

      {tierIdFilter && (
        <FilterBanner
          message={`Showing records on tier "${tierNameFilter || tierIdFilter}"`}
          onClear={clearTierFilter}
        />
      )}

      {closeReasonIdFilter && (
        <FilterBanner
          message={`Showing Prospects closed with reason "${closeReasonNameFilter || closeReasonIdFilter}"`}
          onClear={clearCloseReasonFilter}
        />
      )}

      {staleFilter && (
        <FilterBanner message="Showing records with stale Opening Hours" onClear={clearStaleFilter} />
      )}

      {addressStaleFilter && (
        <FilterBanner message="Showing records with a stale Address" onClear={clearAddressStaleFilter} />
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <CommercialEntityForm
            initial={null}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
            creationContext={addingCustomer ? 'customer' : 'lead'}
          />
        </div>
      )}

      {view === 'price_groups' ? (
        <CustomerPriceGroupMatrix
          rows={rows}
          productGroups={productPriceGroupOptions}
          productGroupFilter={productPriceGroupId}
          loading={loading}
          onView={(item) => { if (!showForm) handleView(item); }}
        />
      ) : (
      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '22%' }} />
            {showType && <col style={{ width: '10%' }} />}
            {showLegalEntity && <col style={{ width: '9%' }} />}
            {showSource && <col style={{ width: '9%' }} />}
            {showStage && <col style={{ width: '10%' }} />}
            {showCloseReason && <col style={{ width: '10%' }} />}
            {showMarket && <col style={{ width: '10%' }} />}
            {showSegment && <col style={{ width: '12%' }} />}
            {showOperator && <col style={{ width: '10%' }} />}
            {showChannel && <col style={{ width: '10%' }} />}
            {showTier && <col style={{ width: '10%' }} />}
            {showPriceGroup && <col style={{ width: '9%' }} />}
            {showTaxHandling && <col style={{ width: '9%' }} />}
            {showTerms && <col style={{ width: '9%' }} />}
            {showCustomerSince && <col style={{ width: '8%' }} />}
            {showLastPricelistSent && <col style={{ width: '9%' }} />}
            {showLogins && <col style={{ width: '10%' }} />}
            {showAddress && <col style={{ width: '11%' }} />}
            {showState && <col style={{ width: '8%' }} />}
            {showCountry && <col style={{ width: '9%' }} />}
            {showPhone && <col style={{ width: '14%' }} />}
            {showOwner && <col style={{ width: '9%' }} />}
            {showSync && <col style={{ width: '9%' }} />}
            {showGoogleSync && <col style={{ width: '9%' }} />}
            {showQuickbooks && <col style={{ width: '14%' }} />}
            {showStatus && <col style={{ width: '9%' }} />}
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              {showType && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Shape</th>}
              {showLegalEntity && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Legal Entity</th>}
              {showSource && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Source</th>}
              {showStage && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Stage</th>}
              {showCloseReason && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Close Reason</th>}
              {showMarket && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Market</th>}
              {showSegment && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Segment</th>}
              {showOperator && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Operator</th>}
              {showChannel && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Outlet</th>}
              {showTier && (
                <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Tier
                    <FieldHelp fieldKey="customers.tier" pageKey="customers" articleSlug="crm-overview" articleTitle="CRM Overview" label="Tier">Classifies this Customer's commercial relationship — defaults Credit Terms, Payment Term, and Tax Display (inc/ex GST) preference. Independent of Price Group, which controls what they pay.</FieldHelp>
                  </span>
                </th>
              )}
              {showPriceGroup && (
                <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Price Groups
                    <FieldHelp fieldKey="customers.price-group" pageKey="customers" articleSlug="crm-overview" articleTitle="CRM Overview" label="Price Groups">Number of Price Group assignments this Customer holds — one Customer Price Group per Product Price Group, which together determine what they pay. Independent of Tier, which governs credit terms, payment timing, and GST display.</FieldHelp>
                  </span>
                </th>
              )}
              {showTaxHandling && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Tax Handling</th>}
              {showTerms && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Terms</th>}
              {showCustomerSince && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Customer<br />Since</th>}
              {showLastPricelistSent && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Last Pricelist<br />Sent</th>}
              {showLogins && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Logins</th>}
              {showAddress && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Address</th>}
              {showState && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>State</th>}
              {showCountry && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Country</th>}
              {showPhone && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Phone</th>}
              {showOwner && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Account Owner</th>}
              {showSync && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Email Sync</th>}
              {showGoogleSync && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Address<br />Hours</th>}
              {showQuickbooks && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>QuickBooks Customer</th>}
              {showStatus && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm) handleView(item); }}
                style={{ cursor: showForm ? 'default' : 'pointer' }}
                title={showForm ? undefined : 'Click to view'}
              >
                <td className="cell-media" style={{ padding: '6px 8px' }}>
                  {item.logo ? (
                    <img src={thumbFileUrl(item.logo.id)} alt="" style={{ display: 'block', margin: '0 auto', width: 42, height: 42, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, padding: 2 }} />
                  ) : item.type === 'individual' ? (
                    <PersonPlaceholderThumb size={42} />
                  ) : (
                    <PlaceholderThumb size={42} />
                  )}
                </td>
                <td className="cell-title" style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>
                  {item.name}
                  {otherRelationshipTypeNames(item).length > 0 && (
                    <span
                      title={`Also tracked as: ${otherRelationshipTypeNames(item).join(', ')}`}
                      style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: '#eceef1', color: '#5a6b85', fontSize: 10, fontWeight: 700, verticalAlign: 'middle' }}
                    >
                      +{otherRelationshipTypeNames(item).length}
                    </span>
                  )}
                </td>
                {showType && <td data-label="Shape" style={{ padding: '6px 8px', fontSize: 14 }}><CategoryPill value={TYPE_LABELS[item.type] || item.type} /></td>}
                {showLegalEntity && <td data-label="Legal Entity" style={{ padding: '6px 8px', fontSize: 14 }}>{item.legal_entity_type?.name || '—'}</td>}
                {showSource && (
                  <td data-label="Source" style={{ padding: '6px 8px', fontSize: 14 }}>
                    {item.source ? <CategoryPill value={item.source.name} /> : '—'}
                  </td>
                )}
                {showStage && (
                  <td data-label="Stage" style={{ padding: '6px 8px', fontSize: 14 }}>
                    {lifecycleRelationship(item)?.stage?.name ? <CategoryPill value={lifecycleRelationship(item).stage.name} /> : '—'}
                  </td>
                )}
                {showCloseReason && <td data-label="Close Reason" style={{ padding: '6px 8px', fontSize: 14 }}>{lifecycleRelationship(item)?.close_reason?.name || '—'}</td>}
                {showMarket && <td data-label="Market" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="market" /></td>}
                {showSegment && <td data-label="Segment" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="segment" /></td>}
                {showOperator && <td data-label="Operator" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="operator" /></td>}
                {showChannel && <td data-label="Outlet" style={{ padding: '6px 8px', fontSize: 14 }}><PrimaryClassificationCell entity={item} level="channel" /></td>}
                {showTier && <td data-label="Tier" style={{ padding: '6px 8px', fontSize: 14 }}>{item.tier?.name || '—'}</td>}
                {showPriceGroup && <td data-label="Price Groups" style={{ padding: '6px 8px', fontSize: 14 }}>{item.price_groups?.length ?? 0}</td>}
                {showTaxHandling && <td data-label="Tax Handling" style={{ padding: '6px 8px', fontSize: 14 }}>{item.tax_handling_type?.name || '—'}</td>}
                {showTerms && <td data-label="Terms" style={{ padding: '6px 8px', fontSize: 14 }}>{item.payment_term?.name || '—'}</td>}
                {showCustomerSince && <td data-label="Customer Since" style={{ padding: '6px 8px', fontSize: 14 }}>{formatDate(item.customer_since) || '—'}</td>}
                {showLastPricelistSent && (
                  <td data-label="Last Pricelist Sent" title={item.last_pricelist_sent?.pricelist_name} style={{ padding: '6px 8px', fontSize: 14 }}>
                    {item.last_pricelist_sent ? formatDate(item.last_pricelist_sent.sent_on) : '—'}
                  </td>
                )}
                {showLogins && <td data-label="Logins" style={{ padding: '6px 8px', fontSize: 14 }}><LoginsCell logins={item.logins} /></td>}
                {showAddress && <td data-label="Address" style={{ padding: '6px 8px', fontSize: 14 }}>{formatAddress(item.primary_address) || '—'}</td>}
                {showState && <td data-label="State" style={{ padding: '6px 8px', fontSize: 14 }}>{item.primary_address?.state || '—'}</td>}
                {showCountry && <td data-label="Country" style={{ padding: '6px 8px', fontSize: 14 }}>{item.primary_address?.country || '—'}</td>}
                {showPhone && <td data-label="Phone" style={{ padding: '6px 8px', fontSize: 14 }}>{item.phones?.length ? item.phones.map((p) => p.phone_number).join(', ') : '—'}</td>}
                {showOwner && (
                  <td data-label="Account Owner" style={{ padding: '6px 8px', fontSize: 14 }}>
                    {item.account_owner ? `${item.account_owner.name}${item.account_owner.is_active ? '' : ' (switched off)'}` : '—'}
                  </td>
                )}
                {showSync && (
                  <td data-label="Email Sync" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.sync} activeLabel="On" inactiveLabel="Off" />
                  </td>
                )}
                {showGoogleSync && (
                  <td data-label="Address/Hours" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    {item.type === 'individual' ? '—' : (
                      <GoogleLookupPill
                        stale={item.opening_hours_stale || item.address_stale}
                        noMatch={item.opening_hours_google_no_match}
                        untried={(item.opening_hours_stale && !item.opening_hours_checked_at) || (item.address_stale && !item.primary_address?.updatedAt)}
                        staleLabel="Stale"
                        checking={refreshingId === item.id}
                        onClick={(e) => handleRefreshStale(e, item)}
                      />
                    )}
                  </td>
                )}
                {showQuickbooks && (
                  <td data-label="QuickBooks Customer" style={{ padding: '6px 8px', fontSize: 13 }}>
                    {item.quickbooks_link ? (
                      <>
                        <div>{item.quickbooks_link.qbo_customer_name}</div>
                        <QuickBooksIssuePills issues={item.quickbooks_link.issues} />
                      </>
                    ) : <span style={{ color: '#888' }}>Not linked</span>}
                  </td>
                )}
                {showStatus && (
                  <td data-label="Status" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.is_active} />
                  </td>
                )}
                <td className="cell-actions" style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'view', label: 'View', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleView(item) },
                      item.type === 'individual' && { key: 'convert-to-organisation', label: 'Convert to Organisation', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleConvertToOrganisation(item) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                      !item.is_active && { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
                    ].filter(Boolean)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel={totalLabel}
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default CommercialEntityTable;
